/**
 * Pure helpers of the staff importer (no Firestore).
 *
 * The staff sheet is positional (Staff Management Portal upload): row 1 is the
 * header, then Staff No, Name, Job, Nationality, Passport No, Card Type, Card
 * Number, Card Expiry, Passport Issue, Passport Expiry, Email, Joining Date.
 * The legacy Save REPLACED the whole `staff` collection with the list: every
 * doc whose id is not an incoming staff number is deleted (including "shadow"
 * duplicates of a kept staff number stored under another id).
 */

/** Excel serial / dd/mm/yyyy / yyyy-mm-dd -> Date, or null. */
export const parseFlexibleDate = (value) => {
    if (!value) return null;
    if (!isNaN(value) && Number(value) > 20000) {
        const date = new Date(Math.round((Number(value) - 25569) * 86400 * 1000));
        return !isNaN(date.getTime()) ? date : null;
    }
    const strVal = String(value).trim();
    if (strVal.includes('/')) {
        const parts = strVal.split('/');
        if (parts.length === 3) {
            const date = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
            return !isNaN(date.getTime()) ? date : null;
        }
    }
    if (strVal.includes('-')) {
        const date = new Date(strVal);
        return !isNaN(date.getTime()) ? date : null;
    }
    return null;
};

/**
 * A date cell of the staff sheet as it is stored. The workbook is read without
 * `cellDates`, so a real Excel date cell arrives as its serial number (45123)
 * and was stored like that (audit ADM-06): it becomes "YYYY-MM-DD". Text dates
 * ("12/05/2020", "2020-05-12") and empty cells are kept exactly as typed.
 */
export const sheetDateOf = (value) => {
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
    const isSerial = typeof value === 'number' || (typeof value === 'string' && /^\d{5}(\.\d+)?$/.test(value.trim()));
    if (isSerial && Number(value) > 20000) {
        const date = parseFlexibleDate(value);
        return date ? date.toISOString().slice(0, 10) : value;
    }
    return value;
};

/** Whole years since a joining date (any format parseFlexibleDate reads), or null. */
export const yearsSince = (joiningDate, now = new Date()) => {
    const date = parseFlexibleDate(joiningDate);
    return date ? Math.floor((now - date) / (1000 * 60 * 60 * 24 * 365.25)) : null;
};

const daysUntil = (date, now) => (date ? Math.ceil((date.getTime() - now.getTime()) / (1000 * 3600 * 24)) : 0);

/** Rows (array of arrays, header first) -> employees in the portal's shape (`staffNo`, `name`, ...). */
export const processStaffRows = (rows, now = new Date()) => {
    const employees = [];
    for (let i = 1; i < (rows || []).length; i++) {
        const row = rows[i] || [];
        const staffNo = row[0] ? String(row[0]).trim() : '';
        const name = row[1];
        if (!staffNo && !name) continue;
        const cardExpiryDate = parseFlexibleDate(row[7]);
        const daysUntilExpiry = daysUntil(cardExpiryDate, now);
        const years = yearsSince(row[11], now);
        employees.push({
            staffNo,
            name,
            job: row[2],
            nationality: row[3],
            passportNumber: row[4],
            cardType: row[5],
            cardNumber: row[6],
            cardExpiry: cardExpiryDate,
            passportIssueDate: sheetDateOf(row[8]),
            passportExpireDate: sheetDateOf(row[9]),
            email: row[10],
            joiningDate: sheetDateOf(row[11]),
            years,
            daysUntilExpiry
        });
    }
    return employees;
};

const normPassport = (value) => String(value ?? '').replace(/\s+/g, '').toUpperCase();
const isCanonical = (d) => d.id === String(d.staff_no ?? '').trim();
const PASSPORT_LIKE_RE = /^(?=(?:[A-Z0-9]*\d){5})[A-Z0-9]{6,12}$/;
const looksLikePassport = (value) => PASSPORT_LIKE_RE.test(normPassport(value));

/**
 * Field view of an existing `staff` doc. Docs written by the legacy Staff
 * Management upload are column-shifted (`name` holds the passport number,
 * `passport_number` the nationality, `job` the name, `nationality` the job);
 * they are read back in their real meaning so matching and "keep the existing
 * value" work, and the next write stores every field under its own name.
 */
export const staffDocFields = (d) => {
    if (!d) return null;
    const shifted = looksLikePassport(d.name) && !looksLikePassport(d.passport_number) && /[A-Za-z]/.test(String(d.job ?? ''));
    return shifted
        ? { ...d, name: d.job ?? '', passport_number: d.name ?? '', job: d.nationality ?? '', nationality: d.passport_number ?? '', shifted: true }
        : { ...d, shifted: false };
};

/** Letters and digits that are often typed for each other: HBF401520 ~ H8F401520, 50642690 ~ S0642690. */
const CONFUSABLES = { '0': 'O', 'O': '0', '1': 'I', 'I': '1', '5': 'S', 'S': '5', '8': 'B', 'B': '8', '2': 'Z', 'Z': '2' };
/** True when two passport numbers differ by a single confusable character or by a leading zero / letter. */
export const passportsAlike = (a, b) => {
    const x = normPassport(a), y = normPassport(b);
    if (!x || !y) return false;
    if (x === y) return true;
    if (x.length === y.length) {
        let diff = 0;
        for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) { if (CONFUSABLES[x[i]] !== y[i]) return false; diff++; }
        return diff === 1;
    }
    const [short, long] = x.length < y.length ? [x, y] : [y, x];
    return long.length - short.length <= 2 && long.endsWith(short) && short.length >= 6;
};

const nameKey = (value) => String(value ?? '').toUpperCase().replace(/[^A-Z ]/g, ' ').replace(/\s+/g, ' ').trim();
/** Two names are the same person when they share at least 3 tokens, or all tokens of the shorter one (2+). */
export const namesAlike = (a, b) => {
    const ta = new Set(nameKey(a).split(' ').filter(Boolean));
    const tb = new Set(nameKey(b).split(' ').filter(Boolean));
    if (ta.size < 2 || tb.size < 2) return false;
    const common = [...ta].filter(t => tb.has(t)).length;
    return common >= 3 || common === Math.min(ta.size, tb.size);
};

/**
 * The existing doc for a PDF record, or null. Cascade: exact passport ->
 * card number -> passport with one typo -> same name. `how` says which key
 * matched so that anything but the exact passport can be reviewed.
 */
export const matchStaffDoc = (e, docs) => {
    const canonicalFirst = (list) => list.find(isCanonical) || list[0] || null;
    const pass = normPassport(e.passportNumber);
    const byPass = pass ? docs.filter(d => normPassport(d.passport_number) === pass) : [];
    if (byPass.length) return { doc: canonicalFirst(byPass), how: 'passport' };
    const card = String(e.cardNumber || '').trim();
    const byCard = card ? docs.filter(d => String(d.card_number || '').trim() === card) : [];
    if (byCard.length) return { doc: canonicalFirst(byCard), how: 'card number' };
    const byTypo = pass ? docs.filter(d => passportsAlike(d.passport_number, pass)) : [];
    if (byTypo.length === 1) return { doc: byTypo[0], how: `passport ${byTypo[0].passport_number} (one character differs)` };
    const byName = e.nameEn ? docs.filter(d => namesAlike(d.name, e.nameEn)) : [];
    if (byName.length === 1) return { doc: byName[0], how: `name "${byName[0].name}"` };
    return { doc: null, how: '' };
};

/**
 * MOHRE "List of Employees" PDF records (see mohreList.js) -> employees in
 * the portal's shape. The staff number comes from the existing `staff` doc
 * matched by passport number, else card number, else a passport with one
 * typo, else the name (see matchStaffDoc; anything but the exact passport is
 * listed for review); an employee with no such doc gets the card number as
 * staff number (`matched: false`). The PDF has
 * no email / joining date / passport dates, and a broken record may miss
 * more: every field the PDF does not provide keeps the existing doc's value,
 * so nothing is blanked. `name` is the English name, else the existing name,
 * else the Arabic name.
 */
export const staffFromMohre = (mohreEmployees, existingDocs, now = new Date()) => {
    const docs = (existingDocs || []).map(staffDocFields);
    return (mohreEmployees || []).map(e => {
        const { doc: existing, how } = matchStaffDoc(e, docs);
        const issues = [...(e.issues || [])];
        if (existing && how !== 'passport') issues.push(`matched staff #${existing.staff_no || existing.id} by ${how}`);
        const pick = (pdfValue, key) => (pdfValue ? pdfValue : (existing?.[key] ?? ''));
        const staffNo = existing ? String(existing.staff_no || existing.id).trim() : String(e.cardNumber || '').trim();
        const stored = existing?.card_expiry ? new Date(existing.card_expiry) : null;
        const cardExpiry = e.cardExpiry || (stored && !isNaN(stored) ? stored : null);
        const joiningDate = existing?.joining_date || null;
        return {
            staffNo,
            name: e.nameEn || existing?.name || e.nameAr || '',
            job: pick(e.job, 'job'),
            nationality: pick(e.nationality, 'nationality'),
            passportNumber: pick(e.passportNumber, 'passport_number'),
            cardType: pick(e.cardType, 'card_type'),
            cardNumber: pick(e.cardNumber, 'card_number'),
            cardExpiry,
            passportIssueDate: existing?.passport_issue_date || null,
            passportExpireDate: existing?.passport_expiry || null,
            email: existing?.email || '',
            joiningDate,
            years: yearsSince(joiningDate, now),
            daysUntilExpiry: daysUntil(cardExpiry, now),
            matched: !!existing,
            matchedBy: existing ? how : '',
            needsReview: issues.length > 0,
            issues
        };
    });
};

const isoOf = (value) => {
    if (value instanceof Date) return isNaN(value) ? null : value.toISOString();
    if (value && !isNaN(new Date(value))) return new Date(value).toISOString();
    return null;
};

/** Employee -> the `staff/<staffNo>` document the legacy sync wrote (without `last_updated`). */
export const staffDocOf = (employee) => ({
    staff_no: String(employee.staffNo).trim(),
    name: employee.name,
    passport_number: employee.passportNumber,
    job: employee.job,
    nationality: employee.nationality,
    card_type: employee.cardType,
    card_number: employee.cardNumber,
    card_expiry: isoOf(employee.cardExpiry),
    passport_issue_date: employee.passportIssueDate || null,
    passport_expiry: employee.passportExpireDate || null,
    email: employee.email || '',
    joining_date: employee.joiningDate || null,
    years: employee.years || null,
    status: 'active'
});

/** Why a MOHRE PDF may not drive removals, or null when it may (a sheet always may: `pdf` null). */
export const removalBlockReason = (pdf, employees) => {
    if (!pdf) return null;
    const list = employees || [];
    if (typeof pdf.total !== 'number') return 'the PDF has no "Total Number of Employees" line';
    if (pdf.total !== list.length) return `the PDF says ${pdf.total} employee(s) but ${list.length} were parsed`;
    const noPassport = list.filter(e => !e.passportNumber).length;
    if (noPassport > 0) return `${noPassport} parsed record(s) have no passport number`;
    return null;
};

/**
 * The removals a staff import may make. An incomplete MOHRE PDF (see
 * removalBlockReason) removes nobody: the staff missing from it are `kept`
 * and only listed, as the importer's hint promises (audit ADM-06).
 * Returns `{ removes, kept, blockReason }`.
 */
export const staffRemovalsOf = (pdf, employees, removes) => {
    const blockReason = removalBlockReason(pdf, employees);
    return blockReason
        ? { removes: [], kept: removes || [], blockReason }
        : { removes: removes || [], kept: [], blockReason: null };
};

const same = (a, b) => String(a ?? '') === String(b ?? '');

/**
 * Replace-style diff of the incoming employees against the whole `staff`
 * collection (`existingDocs`: [{ id, ...data }]):
 *   adds      - staff numbers with no doc
 *   updates   - docs whose stored fields differ ({ id, write, changed })
 *   unchanged - identical docs
 *   removes   - every other doc id: staff missing from the file, and ids that
 *               are not the staff number of the doc they hold (shadow duplicates)
 *   invalid   - rows without a staff number (never written)
 */
export const diffStaff = (employees, existingDocs) => {
    const incoming = new Map();
    let invalid = 0;
    (employees || []).forEach(e => {
        const id = e.staffNo ? String(e.staffNo).trim() : '';
        if (!id) { invalid++; return; }
        incoming.set(id, staffDocOf(e));
    });
    const byId = new Map((existingDocs || []).map(d => [d.id, d]));
    const adds = [];
    const updates = [];
    const unchanged = [];
    incoming.forEach((write, id) => {
        const existing = byId.get(id);
        if (!existing) { adds.push({ id, write }); return; }
        const changed = Object.keys(write).filter(k => !same(existing[k], write[k]));
        if (changed.length > 0) updates.push({ id, write, changed });
        else unchanged.push(id);
    });
    const removes = [];
    byId.forEach((d, id) => {
        if (!incoming.has(id)) removes.push({ id, staff_no: d.staff_no || '', name: d.name || '', shadow: incoming.has(String(d.staff_no ?? '').trim()) });
    });
    return { adds, updates, unchanged, removes, invalid };
};
